"use client";

import { useMemo, useState } from "react";
import { MARQUES, FAMILLES, type Marque, type Produit } from "@/data/produits";

/** Compteurs dérivés des données — mis à jour à chaque build_site_data.py */
const NB_VEDETTES = MARQUES.reduce((a, m) => a + m.produits.length, 0);
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Search,
  Crown,
  Gem,
  Layers,
  Store,
  Sparkles,
  MapPin,
  ArrowRight,
} from "lucide-react";

const BASE = "/produits";
const MAITRES = "/maitres";

function norm(s: string) {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const HERO_MAIN = {
  folder: "04_SULTAN_DES_DELICES",
  file: "P1_Coffret_Imperial_Assortiments.webp",
  nom: "Coffret Impérial Assortiments",
};
const HERO_SIDE_1 = {
  folder: "34_SULJAKA",
  file: "P1_Veste_Cuir_Noir_Royal.webp",
  nom: "Veste Cuir Noir Royal",
};
const HERO_SIDE_2 = {
  folder: "10_SULPARFUN",
  file: "P1_Eau_de_Parfum_Sultana_Or.webp",
  nom: "Eau de Parfum Sultana Or",
};

const DECORS = [
  {
    nom: "Velours bordeaux froissé",
    folder: "34_SULJAKA",
    file: "P1_Veste_Cuir_Noir_Royal.webp",
    desc: "Rideaux de velours écarlate aux plis sculpturaux — la signature théâtrale de la Maison.",
  },
  {
    nom: "Velours émeraude froissé",
    folder: "04_SULTAN_DES_DELICES",
    file: "P1_Coffret_Imperial_Assortiments.webp",
    desc: "Velours vert bijou aux plis profonds — l'émeraude des sultans, sublimée par l'or.",
  },
  {
    nom: "Satin champagne",
    folder: "29_SULBULANGERIE",
    file: "P1_Baguette_Tradition_du_Sultan.webp",
    desc: "Vagues lumineuses de satin ivoire, la chaleur dorée d'un soleil de fin d'après-midi.",
  },
  {
    nom: "Velours bleu nuit",
    folder: "10_SULPARFUN",
    file: "P1_Eau_de_Parfum_Sultana_Or.webp",
    desc: "Velours d'un bleu de minuit, profond comme le ciel d'un palais oriental.",
  },
  {
    nom: "Noyer sombre & halo bronze",
    folder: "02_SULGOURDES",
    file: "P2_Jus_de_Bissap_Rouge_Rubis.webp",
    desc: "Bois de noyer patiné sous un halo de bronze, l'atelier d'un maître ébéniste.",
  },
  {
    nom: "Soie jade plissée",
    folder: "09_SULMIEL",
    file: "P3_Miel_de_Foret_Sauvage.webp",
    desc: "Flots de soie jade aux reflets lustrés, une lumière de jade sous la lampe dorée.",
  },
  {
    nom: "Satin sauge lumineux",
    folder: "18_SULBEAUTE",
    file: "P3_Huile_Precieuse_Baobab.webp",
    desc: "Vagues de satin sauge, la fraîcheur d'un jardin de palais au matin.",
  },
  {
    nom: "Bois d'olivier & mur forêt",
    folder: "16_SULPALAIS",
    file: "P2_Parure_de_Lit_Satin_Or_Emeraude.webp",
    desc: "Bois d'olivier patiné sous un mur vert forêt et son halo doré — les matières nobles.",
  },
];

function FadeIn({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionTitle({
  eyebrow,
  titre,
  italique,
  sous,
}: {
  eyebrow: string;
  titre: string;
  italique?: string;
  sous?: string;
}) {
  return (
    <FadeIn className="text-center max-w-3xl mx-auto">
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="font-maison text-4xl md:text-5xl mt-4 text-[#f3e9d2]">
        {titre}{" "}
        {italique ? <em className="text-[#f0d67a]">{italique}</em> : null}
      </h2>
      <div className="filet-losange w-56 mx-auto mt-6" />
      {sous ? (
        <p className="text-[#b39b72] mt-5 leading-relaxed text-[15px]">{sous}</p>
      ) : null}
    </FadeIn>
  );
}

function Header() {
  const liens = [
    ["La Maison", "#maison"],
    ["Décors", "#decors"],
    ["Collection", "#collection"],
    ["Marques", "#marques"],
  ] as const;
  return (
    <header className="sticky top-0 z-50 border-b border-[rgba(212,175,55,0.22)] bg-[#131009]/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center gap-8">
        <a href="#" className="flex items-center gap-3 shrink-0">
          <span className="grid place-items-center w-9 h-9 rounded-full border border-[#d4af37]/60">
            <Crown className="w-4 h-4 text-[#f0d67a]" />
          </span>
          <span className="font-maison tracking-[0.18em] text-[15px] text-[#f3e9d2]">
            SULTAN <span className="text-[#f0d67a]">DES DELICES</span>
          </span>
        </a>
        <nav aria-label="Navigation principale" className="hidden md:flex items-center gap-7 ml-auto">
          {liens.map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="text-[12px] tracking-[0.22em] uppercase text-[#b39b72] hover:text-[#f0d67a] transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>
        <span className="ml-auto md:ml-0 hidden sm:inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-[#d4af37] border border-[#d4af37]/35 rounded-full px-3.5 py-1.5">
          <MapPin className="w-3 h-3" /> Dakar — Paris
        </span>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 halo-or pointer-events-none" aria-hidden />
      <div className="max-w-7xl mx-auto px-5 pt-16 pb-14 md:pt-24 md:pb-20 grid lg:grid-cols-2 gap-12 items-center relative">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="eyebrow">Maison de Prestige — Collection Vedettes</p>
            <h1 className="font-maison text-[42px] leading-[1.08] md:text-6xl mt-5 text-[#f3e9d2]">
              L&apos;Art du Goût,
              <br />
              <em className="text-[#f0d67a]">à la hauteur d&apos;un Sultan</em>
            </h1>
            <p className="text-[#b39b72] mt-6 max-w-xl leading-relaxed text-[15px]">
              {MARQUES.length} marques, {NB_VEDETTES} pièces d&apos;exception. Chaque vedette
              est photographiée dans l&apos;univers couture « Sadiya » : velours bordeaux froissé,
              velours émeraude, soie jade, satin champagne et lumière dorée — tandis que chaque
              produit garde ses couleurs naturelles, véritables.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Button
                asChild
                className="bg-[#d4af37] hover:bg-[#f0d67a] text-[#131009] font-medium tracking-wide rounded-none px-7"
              >
                <a href="#collection">
                  Explorer la collection <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-none border-[#d4af37]/45 text-[#f0d67a] hover:bg-[#d4af37]/10 hover:text-[#f0d67a] px-7"
              >
                <a href="#decors">L&apos;univers des décors</a>
              </Button>
            </div>
            <div className="flex gap-8 mt-10 text-[#f3e9d2]">
              {[
                [String(MARQUES.length), "marques"],
                [String(NB_VEDETTES), "vedettes"],
                ["8", "décors couture"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-maison text-3xl text-[#f0d67a]">{n}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#b39b72] mt-1">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          <div className="absolute -inset-8 halo-or" aria-hidden />
          <div className="relative mx-auto max-w-[520px] cadre-or">
            <img
              src={`${BASE}/${HERO_MAIN.folder}/${HERO_MAIN.file}`}
              alt={HERO_MAIN.nom}
              className="w-full aspect-square object-cover"
            />
            <div className="absolute bottom-4 left-4 bg-[#131009]/80 backdrop-blur-sm border border-[#d4af37]/40 px-4 py-2">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37]">
                Pièce d&apos;exception
              </div>
              <div className="font-maison text-[#f3e9d2]">{HERO_MAIN.nom}</div>
            </div>
          </div>

          <div className="hidden lg:block absolute -left-12 top-10 w-40 cadre-or rotate-[-5deg] shadow-2xl">
            <img
              src={`${BASE}/${HERO_SIDE_1.folder}/${HERO_SIDE_1.file}`}
              alt={HERO_SIDE_1.nom}
              className="w-full aspect-square object-cover"
            />
          </div>
          <div className="hidden lg:block absolute -right-10 bottom-12 w-44 cadre-or rotate-[4deg] shadow-2xl">
            <img
              src={`${BASE}/${HERO_SIDE_2.folder}/${HERO_SIDE_2.file}`}
              alt={HERO_SIDE_2.nom}
              className="w-full aspect-square object-cover"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const noms = MARQUES.map((m) => m.marque);
  const piste = [...noms, ...noms];
  return (
    <div className="border-y border-[rgba(212,175,55,0.18)] bg-[#171208] py-4 overflow-hidden">
      <div className="marquee-track">
        {piste.map((n, i) => (
          <span
            key={i}
            className="font-maison italic text-[#b39b72] text-lg px-6 whitespace-nowrap"
          >
            {n} <span className="text-[#d4af37] not-italic text-xs align-middle px-6">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function MaisonSection() {
  const maitre = MARQUES.find((m) => m.num === "04")?.maitre;
  return (
    <section id="maison" className="scroll-mt-24 py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
        <FadeIn>
          <p className="eyebrow">La Maison</p>
          <h2 className="font-maison text-4xl md:text-5xl mt-4 text-[#f3e9d2]">
            De l&apos;héritage à la <em className="text-[#f0d67a]">création</em>
          </h2>
          <div className="filet-losange w-48 mt-6 mb-7" />
          <div className="space-y-5 text-[#b39b72] leading-relaxed text-[15px]">
            <p>
              SULTAN DES DELICES réunit {MARQUES.length} marques sous une même bannière : celle du
              goût véritable, de l&apos;épicerie fine au high-tech, du bazin brodé à la
              maroquinerie de vachette. Chaque marque est une maison dans la Maison, avec sa
              grammaire, ses gestes et ses couleurs.
            </p>
            <p>
              Pour la Collection Vedettes, le studio a emprunté son regard à la grande
              maroquinerie parisienne : décors de velours froissé, de soie et de satin, lumière
              dorée en contre-jour, ombres ciselées. Une mise en scène intime où la moindre
              couture devient un geste d&apos;orfèvre.
            </p>
            <p>
              Fidèle à la consigne de la Maison, chaque produit conserve ses couleurs naturelles
              — verts, ors, ambres, noirs, blancs, rouge Bordeaux et bleus — tandis que le
              décor prêtant son éclat reste strictement en arrière-plan. Le résultat : {NB_VEDETTES}{" "}
              images qui regardent le spectateur comme un bijou dans son écrin.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.12} className="relative">
          <div className="absolute -inset-6 halo-or" aria-hidden />
          <div className="relative cadre-or max-w-[480px] mx-auto">
            {maitre ? (
              <img
                src={`${MAITRES}/${maitre}`}
                alt="Visuel maître SULTAN DES DELICES"
                className="w-full aspect-square object-cover"
              />
            ) : null}
            <div className="absolute bottom-4 left-4 bg-[#131009]/80 backdrop-blur-sm border border-[#d4af37]/40 px-4 py-2">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37]">
                Visuel maître
              </div>
              <div className="font-maison text-[#f3e9d2]">Marque ombrelle n°04</div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

function DecorsSection() {
  return (
    <section id="decors" className="scroll-mt-24 py-20 md:py-24 bg-[#171208] border-y border-[rgba(212,175,55,0.14)]">
      <div className="max-w-7xl mx-auto px-5">
        <SectionTitle
          eyebrow="Le Studio"
          titre="Huit décors,"
          italique="huit lumières"
          sous="Chaque vedette est mise en scène sur l'un des huit décors couture du studio Sadiya — velours émeraude, soie jade, satin sauge, bois d'olivier — éclairée en low-key doré, comme au temps des grandes maisons."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {DECORS.map((d, i) => (
            <FadeIn key={d.nom} delay={i * 0.07}>
              <figure className="group border border-[rgba(212,175,55,0.22)] bg-[#1d1712] overflow-hidden h-full transition-colors hover:border-[#d4af37]/60">
                <div className="overflow-hidden">
                  <img
                    src={`${BASE}/${d.folder}/${d.file}`}
                    alt={`Exemple de décor : ${d.nom}`}
                    loading="lazy"
                    className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <figcaption className="p-4">
                  <div className="font-maison text-[#f3e9d2] text-lg">{d.nom}</div>
                  <p className="text-[#b39b72] text-[13px] leading-relaxed mt-2">{d.desc}</p>
                </figcaption>
              </figure>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

type Selection = { marque: Marque; produit: Produit };

function CollectionSection({
  famille,
  setFamille,
  q,
  setQ,
  onOpen,
}: {
  famille: string;
  setFamille: (v: string) => void;
  q: string;
  setQ: (v: string) => void;
  onOpen: (s: Selection) => void;
}) {
  const marquesFiltrees = useMemo(() => {
    const nq = norm(q.trim());
    return MARQUES.map((m) => ({
      ...m,
      produits: m.produits.filter(
        (p) =>
          (famille === "all" || m.famille === famille) &&
          (!nq || norm(p.nom).includes(nq) || norm(m.marque).includes(nq))
      ),
    })).filter((m) => m.produits.length > 0);
  }, [famille, q]);

  const nbPieces = marquesFiltrees.reduce((a, m) => a + m.produits.length, 0);

  return (
    <section id="collection" className="scroll-mt-24 py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-5">
        <SectionTitle
          eyebrow="La Collection"
          titre="Les pièces"
          italique="vedettes"
          sous={`${NB_VEDETTES} packshots, ${MARQUES.length} marques, une même exigence. Cliquez sur une pièce pour découvrir sa fiche.`}
        />

        <FadeIn className="mt-12">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setFamille("all")}
              className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                famille === "all"
                  ? "border-[#d4af37] text-[#f0d67a] bg-[#d4af37]/10"
                  : "border-[rgba(212,175,55,0.25)] text-[#b39b72] hover:text-[#f0d67a] hover:border-[#d4af37]/60"
              }`}
            >
              Tout voir
            </button>
            {FAMILLES.map((f) => (
              <button
                key={f}
                onClick={() => setFamille(f)}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                  famille === f
                    ? "border-[#d4af37] text-[#f0d67a] bg-[#d4af37]/10"
                    : "border-[rgba(212,175,55,0.25)] text-[#b39b72] hover:text-[#f0d67a] hover:border-[#d4af37]/60"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-7">
            <p className="text-[12px] tracking-[0.2em] uppercase text-[#b39b72]">
              {marquesFiltrees.length} {marquesFiltrees.length > 1 ? "marques" : "marque"} ·{" "}
              {nbPieces} {nbPieces > 1 ? "pièces" : "pièce"}
            </p>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#b39b72]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un produit ou une marque…"
                className="pl-9 bg-[#1d1712] border-[rgba(212,175,55,0.3)] text-[#f3e9d2] placeholder:text-[#8a744f] focus-visible:ring-[#d4af37]/50"
              />
            </div>
          </div>
        </FadeIn>

        <div className="mt-12 space-y-16">
          {marquesFiltrees.map((m) => (
            <article key={m.num} id={`marque-${m.num}`} className="scroll-mt-28">
              <div className="flex items-baseline gap-4 border-b border-[rgba(212,175,55,0.22)] pb-3 mb-6">
                <span className="font-maison text-xl text-[#d4af37]">{m.num}</span>
                <h3 className="font-maison text-2xl md:text-3xl text-[#f3e9d2]">{m.marque}</h3>
                <span className="ml-auto text-[11px] uppercase tracking-[0.22em] text-[#b39b72] hidden sm:inline">
                  {m.famille}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {m.produits.map((p) => (
                  <button
                    key={p.file}
                    onClick={() => onOpen({ marque: m, produit: p })}
                    className="group text-left border border-[rgba(212,175,55,0.22)] bg-[#1d1712] overflow-hidden transition-all hover:border-[#d4af37]/70 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(0,0,0,0.55)] focus-visible:outline focus-visible:outline-[#d4af37]"
                    aria-label={`Voir la fiche de ${p.nom}`}
                  >
                    <div className="overflow-hidden">
                      <img
                        src={`${BASE}/${m.folder}/${p.file}`}
                        alt={`${p.nom} — ${m.marque}`}
                        loading="lazy"
                        className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="p-4">
                      <div className="font-maison text-[17px] text-[#f3e9d2] group-hover:text-[#f0d67a] transition-colors">
                        {p.nom}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[11px] uppercase tracking-[0.18em] text-[#b39b72]">
                          {p.decor}
                        </span>
                        <Sparkles className="w-3.5 h-3.5 text-[#d4af37]/70" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </article>
          ))}
          {marquesFiltrees.length === 0 ? (
            <p className="text-center text-[#b39b72] font-maison italic text-xl py-16">
              Aucune pièce ne correspond à cette recherche.
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function MarquesIndex({ onPick }: { onPick: (num: string) => void }) {
  return (
    <section id="marques" className="scroll-mt-24 py-20 bg-[#171208] border-y border-[rgba(212,175,55,0.14)]">
      <div className="max-w-5xl mx-auto px-5">
        <SectionTitle
          eyebrow="Index"
          titre={"Les " + MARQUES.length}
          italique="marques"
          sous="Accédez directement à la maison de votre choix."
        />
        <div className="flex flex-wrap justify-center gap-2.5 mt-10">
          {MARQUES.map((m) => (
            <button
              key={m.num}
              onClick={() => onPick(m.num)}
              title={m.famille}
              className="group px-3.5 py-2 border border-[rgba(212,175,55,0.25)] text-[12px] tracking-wide text-[#b39b72] hover:text-[#131009] hover:bg-[#d4af37] hover:border-[#d4af37] transition-colors"
            >
              <span className="text-[#d4af37] group-hover:text-[#131009]">{m.num}</span>{" "}
              {m.marque}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="mt-auto border-t border-[rgba(212,175,55,0.22)] bg-[#100d07]">
      <div className="max-w-7xl mx-auto px-5 py-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full border border-[#d4af37]/60">
              <Crown className="w-4 h-4 text-[#f0d67a]" />
            </span>
            <span className="font-maison tracking-[0.18em] text-[15px] text-[#f3e9d2]">
              SULTAN <span className="text-[#f0d67a]">DES DELICES</span>
            </span>
          </div>
          <p className="text-[#b39b72] text-[13px] leading-relaxed mt-4">
            Maison de prestige — Collection Vedettes photographiée dans l&apos;univers couture
            « Sadiya Style Cuir » : velours, soie, satin et lumière dorée.
          </p>
        </div>
        <div>
          <p className="eyebrow">La collection</p>
          <ul className="mt-4 space-y-2 text-[13px] text-[#b39b72]">
            <li>{MARQUES.length} marques · {NB_VEDETTES} pièces vedettes</li>
            <li>8 décors couture · lumière low-key dorée</li>
            <li>Packshots 1024 × 1024 — couleurs naturelles</li>
            <li>Visuels maîtres Bleu Nuit Royal &amp; Or</li>
          </ul>
        </div>
        <div>
          <p className="eyebrow">Navigation</p>
          <ul className="mt-4 space-y-2 text-[13px]">
            {[
              ["La Maison", "#maison"],
              ["Les décors", "#decors"],
              ["La collection", "#collection"],
              ["Les marques", "#marques"],
            ].map(([l, h]) => (
              <li key={h}>
                <a href={h} className="text-[#b39b72] hover:text-[#f0d67a] transition-colors">
                  {l}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-[rgba(212,175,55,0.14)]">
        <p className="text-center text-[11px] tracking-[0.25em] uppercase text-[#8a744f] py-5">
          Sultan des Delices — Studio Sadiya · {new Date().getFullYear()}
        </p>
      </div>
    </footer>
  );
}

export default function Page() {
  const [famille, setFamille] = useState("all");
  const [q, setQ] = useState("");
  const [selection, setSelection] = useState<Selection | null>(null);

  const pickMarque = (num: string) => {
    setFamille("all");
    setQ("");
    requestAnimationFrame(() => {
      document
        .getElementById(`marque-${num}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#131009]">
      <Header />
      <main className="flex-1">
        <Hero />
        <Marquee />
        <MaisonSection />
        <DecorsSection />
        <CollectionSection
          famille={famille}
          setFamille={setFamille}
          q={q}
          setQ={setQ}
          onOpen={setSelection}
        />
        <MarquesIndex onPick={pickMarque} />
      </main>
      <ProductDialog selection={selection} onClose={() => setSelection(null)} />
      <Footer />
    </div>
  );
}

function ProductDialog({
  selection,
  onClose,
}: {
  selection: Selection | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!selection} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-4xl p-0 gap-0 bg-[#1d1712] border border-[#d4af37]/35 overflow-hidden">
        {selection ? (
          <div className="grid md:grid-cols-2">
            <div className="relative bg-black">
              <img
                src={`${BASE}/${selection.marque.folder}/${selection.produit.file}`}
                alt={selection.produit.nom}
                className="w-full h-full max-h-[560px] object-cover"
              />
            </div>
            <div className="p-7 md:p-9 flex flex-col">
              <DialogHeader className="text-left space-y-0">
                <div className="flex items-center gap-3">
                  <span className="font-maison text-2xl text-[#d4af37]">
                    {selection.marque.num}
                  </span>
                  <DialogTitle className="font-maison text-2xl md:text-3xl font-normal text-[#f3e9d2]">
                    {selection.marque.marque}
                  </DialogTitle>
                </div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#b39b72] mt-2">
                  {selection.marque.famille}
                </p>
              </DialogHeader>
              <div className="filet-losange w-full my-5" />
              <h3 className="font-maison text-xl text-[#f0d67a]">{selection.produit.nom}</h3>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex gap-3">
                  <dt className="text-[#b39b72] w-24 shrink-0">Décor</dt>
                  <dd className="text-[#f3e9d2]">{selection.produit.decor}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-[#b39b72] w-24 shrink-0">Lumière</dt>
                  <dd className="text-[#f3e9d2]">Low-key dorée, spot zénithal</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-[#b39b72] w-24 shrink-0">Format</dt>
                  <dd className="text-[#f3e9d2]">Packshot 1024 × 1024</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="text-[#b39b72] w-24 shrink-0">Fichier</dt>
                  <dd className="text-[#b39b72] font-mono text-xs break-all">
                    {selection.produit.file}
                  </dd>
                </div>
              </dl>
              {selection.marque.maitre ? (
                <div className="mt-auto pt-6 flex items-center gap-4">
                  <img
                    src={`${MAITRES}/${selection.marque.maitre}`}
                    alt={`Visuel maître ${selection.marque.marque}`}
                    className="w-14 h-14 object-cover border border-[#d4af37]/40"
                  />
                  <p className="text-[12px] text-[#b39b72] leading-snug">
                    Visuel maître de la marque
                    <br />
                    <span className="text-[#f0d67a]">Harmonie Bleu Nuit &amp; Or</span>
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
